import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "./useSubscription";
import { TIER_LIMITS, type PlanTier, TIER_LABELS } from "@/lib/tiers";

/**
 * Counts the user's current resource usage so we can compare it against any
 * tier's limits — used both for "what would break if I downgrade?" warnings
 * AND for "you're currently over your plan limits" banners (post-downgrade).
 */
export interface UsageSnapshot {
  templates: number;
  cyclesThisMonth: number;
  largestCycleContributors: number;
  managerSeats: number;
  executiveViewers: number;
  loading: boolean;
  hasCustomBranding: boolean;
  hasActiveSchedules: boolean;
}

export interface RiskItem {
  label: string;
  current: number | string;
  limit: number | string;
  consequence: string;
}

export function useUsageSnapshot(): UsageSnapshot {
  const { user } = useAuth();
  const enabled = !!user?.id;

  const { data, isLoading } = useQuery({
    queryKey: ["usage-snapshot", user?.id],
    enabled,
    queryFn: async () => {
      const userId = user!.id;

      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);

      // Get user's org ids first since other queries depend on them
      const orgRes = await supabase.from("managers").select("org_id").eq("user_id", userId);
      const orgIds = (orgRes.data ?? []).map((m) => m.org_id);

      const [
        templatesRes,
        cyclesRes,
        cycleContribCounts,
        managerRows,
        executiveRows,
        brandRow,
        schedulesRes,
      ] = await Promise.all([
        supabase.from("report_templates").select("id", { count: "exact", head: true }).eq("user_id", userId),
        supabase
          .from("report_cycles")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId)
          .gte("created_at", monthStart.toISOString()),
        supabase
          .from("report_cycles")
          .select("id, submission_requests:submission_requests(count)")
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(50),
        orgIds.length > 0
          ? supabase.from("managers").select("user_id").in("org_id", orgIds)
          : Promise.resolve({ data: [] as { user_id: string }[] }),
        supabase
          .from("executive_template_access")
          .select("executive_user_id, template_id, report_templates!inner(user_id)")
          .eq("report_templates.user_id", userId),
        supabase.from("brand_settings").select("primary_color, logo_url, preset_name").eq("user_id", userId).maybeSingle(),
        supabase.from("recurring_schedules").select("id", { count: "exact", head: true }).eq("user_id", userId).eq("status", "active"),
      ]);

      const largestCycleContributors = (cycleContribCounts.data ?? []).reduce((max, c: any) => {
        const n = Array.isArray(c.submission_requests) ? c.submission_requests[0]?.count ?? 0 : 0;
        return n > max ? n : max;
      }, 0);

      const uniqueManagerIds = new Set((managerRows.data ?? []).map((m: any) => m.user_id));
      const uniqueExecutives = new Set((executiveRows.data ?? []).map((e: any) => e.executive_user_id));

      const hasCustomBranding = !!(brandRow.data && (brandRow.data.logo_url || (brandRow.data.preset_name && brandRow.data.preset_name !== "default")));

      return {
        templates: templatesRes.count ?? 0,
        cyclesThisMonth: cyclesRes.count ?? 0,
        largestCycleContributors,
        managerSeats: uniqueManagerIds.size || 1,
        executiveViewers: uniqueExecutives.size,
        hasCustomBranding,
        hasActiveSchedules: (schedulesRes.count ?? 0) > 0,
      };
    },
  });

  return {
    templates: data?.templates ?? 0,
    cyclesThisMonth: data?.cyclesThisMonth ?? 0,
    largestCycleContributors: data?.largestCycleContributors ?? 0,
    managerSeats: data?.managerSeats ?? 1,
    executiveViewers: data?.executiveViewers ?? 0,
    hasCustomBranding: data?.hasCustomBranding ?? false,
    hasActiveSchedules: data?.hasActiveSchedules ?? false,
    loading: isLoading,
  };
}

/** Compute what would exceed the target tier's limits given current usage. */
export function computeRisks(usage: UsageSnapshot, targetTier: PlanTier): RiskItem[] {
  const limits = TIER_LIMITS[targetTier];
  const risks: RiskItem[] = [];

  if (usage.templates > limits.maxTemplates) {
    risks.push({
      label: "Templates",
      current: usage.templates,
      limit: limits.maxTemplates,
      consequence: `Over the ${TIER_LABELS[targetTier]} limit of ${limits.maxTemplates}. You can keep existing templates but can't create new ones until you delete some.`,
    });
  }

  if (usage.largestCycleContributors > limits.maxContributorsPerCycle) {
    risks.push({
      label: "Contributors per cycle",
      current: usage.largestCycleContributors,
      limit: limits.maxContributorsPerCycle,
      consequence: `Your largest cycle has more contributors than ${TIER_LABELS[targetTier]} allows. Existing cycles keep working; new cycles will be capped at ${limits.maxContributorsPerCycle}.`,
    });
  }

  if (limits.maxCyclesPerMonth !== Infinity && usage.cyclesThisMonth > limits.maxCyclesPerMonth) {
    risks.push({
      label: "Cycles this month",
      current: usage.cyclesThisMonth,
      limit: limits.maxCyclesPerMonth,
      consequence: `You've already created more cycles this month than ${TIER_LABELS[targetTier]} allows. You won't be able to create new ones until next month.`,
    });
  }

  if (usage.managerSeats > limits.managerSeats) {
    risks.push({
      label: "Manager seats",
      current: usage.managerSeats,
      limit: limits.managerSeats,
      consequence: `${TIER_LABELS[targetTier]} includes ${limits.managerSeats} manager seat${limits.managerSeats === 1 ? "" : "s"}. Extra managers will lose access.`,
    });
  }

  if (limits.executiveViewers !== Infinity && usage.executiveViewers > limits.executiveViewers) {
    risks.push({
      label: "Executive viewers",
      current: usage.executiveViewers,
      limit: limits.executiveViewers,
      consequence: `${TIER_LABELS[targetTier]} allows ${limits.executiveViewers} executive viewer${limits.executiveViewers === 1 ? "" : "s"}. Extras will lose report access.`,
    });
  }

  if (!limits.customBranding && usage.hasCustomBranding) {
    risks.push({
      label: "Custom branding",
      current: "Active",
      limit: "Not included",
      consequence: "Your custom logo and brand colors will revert to defaults on new reports.",
    });
  }

  if (!limits.recurringSchedules && usage.hasActiveSchedules) {
    risks.push({
      label: "Recurring schedules",
      current: "Active",
      limit: "Not included",
      consequence: "Your active schedules will be paused. Existing cycles continue, but no new ones will be auto-created.",
    });
  }

  return risks;
}

/**
 * Convenience: compares current usage against the user's CURRENT tier limits.
 * Use this for the "you're over your plan" banner that shows post-downgrade.
 */
export function useOverLimitRisks(): { risks: RiskItem[]; loading: boolean } {
  const { tier, loading: subLoading } = useSubscription();
  const usage = useUsageSnapshot();
  return {
    risks: computeRisks(usage, tier),
    loading: subLoading || usage.loading,
  };
}
